import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PAGINATION_DEFAULT } from '@core/constants';
import {
  DeviceModel,
  DevicePayloadModel,
  DeviceResponseModel,
  DeviceFiltersModel,
  PartnerModel,
  LocationModel,
  DeviceChannelConfiguration,
  DeviceConfigurationUpdate,
  DeviceConfigurationForDeviceChannel,
  ChannelUpdatePayload,
  DeviceTypeResponseModel,
  ConnectionTestResult,
  LatestStatusResponse,
  Category,
  DeviceConfigurationClear,
  InitialConfigFilesForDevice,
  PhaseCount,
  StartReadoutResult,
  ReadoutStatus,
  InstallationGroupWithAccess,
  MeterRole,
  SimActionResult,
  SimStatusResult,
} from '@core/models';
import {
  DeviceVerifications,
  StartVerificationResult,
  VerificationDetails,
} from '@core/models/verification.model';
import {
  CompanyService,
  DeviceService,
  InstallationGroupService,
  LocationService,
} from '@core/services';
import { StatusService } from '@core/services/status.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { forEach, pickBy } from 'lodash';
import {
  pipe,
  switchMap,
  tap,
  interval,
  concatMap,
  first,
  shareReplay,
  Observable,
  of,
} from 'rxjs';

/** A device sits in only a handful of groups, so fetch them all in one page (no pagination needed). */
const DEVICE_INSTALLATION_GROUPS_LIMIT = 100;

/**
 * The channel-configuration endpoint answers 404 for two unrelated reasons (device-func's
 * getDeviceChannels): the device doesn't exist or is out of the caller's scope, or the device
 * exists but its type doesn't support channels at all. Only the `detail` text tells them apart.
 */
export type ChannelConfigurationErrorReason = 'notSupported' | 'notFound' | 'unknown';

const CHANNELS_NOT_SUPPORTED_DETAIL = 'Device does not support channels';
const DEVICE_NOT_FOUND_DETAIL = 'Device does not exist';

/**
 * ApiService.get() unwraps a failed request to its parsed `.error` body before it reaches a
 * subscriber, discarding the HttpErrorResponse and its status. Read the status when a raw
 * HttpErrorResponse ever does arrive, but fall back to the response body's `detail` text, which
 * is the only signal this endpoint's two 404 cases leave behind.
 */
function classifyChannelConfigurationError(error: unknown): ChannelConfigurationErrorReason {
  const httpError = error instanceof HttpErrorResponse ? error : undefined;
  const body = (httpError ? httpError.error : error) as { detail?: string } | null | undefined;
  const detail = typeof body?.detail === 'string' ? body.detail : undefined;
  if (detail === CHANNELS_NOT_SUPPORTED_DETAIL) return 'notSupported';
  if (detail === DEVICE_NOT_FOUND_DETAIL || httpError?.status === 404) return 'notFound';
  return 'unknown';
}

type DeviceState = {
  isLoading: boolean;
  isLoadingDeviceConfigurations: boolean;
  isLoadingVerifications: boolean;
  isLoadingVerificationDetails: boolean;
  isLoadingConfiguration: boolean;
  isLoadingReadoutStatus: boolean;
  isLoadingInitialConfigFiles: boolean;
  deviceData: DeviceResponseModel;
  deviceTypes: DeviceTypeResponseModel | undefined;
  deviceDetail: DeviceModel | undefined;
  // The owner-only phase-count read answered 403: the user reaches this device through a
  // partnership, and device-func refuses the meter role and phase count to anyone but the owner.
  phaseCountReadForbidden: boolean;
  partnerByDeviceId: PartnerModel | undefined;
  locationByDeviceId: LocationModel | undefined;
  filter: DeviceFiltersModel;
  channelConfiguration: DeviceChannelConfiguration | undefined;
  channelConfigurationError: ChannelConfigurationErrorReason | undefined;
  deviceConfigurations: DeviceConfigurationForDeviceChannel[];
  isExecutingConnectionTest: boolean;
  connectionTestResult: ConnectionTestResult | undefined;
  categories: Category[] | undefined;
  verificationAttempts: DeviceVerifications | undefined;
  verificationDetails: VerificationDetails | undefined;
  readoutStatus: ReadoutStatus | undefined;
  initialConfigFiles: InitialConfigFilesForDevice | undefined;
  installationGroupsByDeviceId: InstallationGroupWithAccess[];
  isLoadingInstallationGroups: boolean;
};

const initialState: DeviceState = {
  isLoading: false,
  isLoadingDeviceConfigurations: false,
  isLoadingVerifications: false,
  isLoadingVerificationDetails: false,
  isLoadingConfiguration: false,
  isLoadingReadoutStatus: false,
  deviceTypes: undefined,
  deviceData: {
    devices: [],
    numberOfItems: 0,
  },
  deviceDetail: undefined,
  phaseCountReadForbidden: false,
  filter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },
  partnerByDeviceId: undefined,
  locationByDeviceId: undefined,
  channelConfiguration: undefined,
  channelConfigurationError: undefined,
  deviceConfigurations: [],
  isExecutingConnectionTest: false,
  connectionTestResult: undefined,
  categories: undefined,
  verificationAttempts: undefined,
  verificationDetails: undefined,
  readoutStatus: undefined,
  isLoadingInitialConfigFiles: false,
  initialConfigFiles: undefined,
  installationGroupsByDeviceId: [],
  isLoadingInstallationGroups: false,
};

export const DeviceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      deviceService = inject(DeviceService),
      partnerService = inject(CompanyService),
      locationService = inject(LocationService),
      statusService = inject(StatusService),
      installationGroupService = inject(InstallationGroupService),
    ) => {
      /** Refetch the groups the device currently belongs to (single full page). */
      const fetchInstallationGroupsForDevice = (deviceId: number) =>
        installationGroupService
          .getInstallationGroups({
            deviceId,
            limit: DEVICE_INSTALLATION_GROUPS_LIMIT,
            offset: 0,
          })
          .pipe(
            tapResponse({
              next: response =>
                patchState(store, {
                  installationGroupsByDeviceId: response.installationGroups,
                  isLoadingInstallationGroups: false,
                }),
              error: () => patchState(store, { isLoadingInstallationGroups: false }),
            }),
          );

      const getCategoriesMethod = rxMethod<void>(
        pipe(
          switchMap(() => {
            return deviceService.getCategories().pipe(
              shareReplay(1),
              tapResponse({
                next: categories => {
                  patchState(store, {
                    categories,
                  });
                },
                error: () => {},
              }),
            );
          }),
        ),
      );
      const getChannelConfigurationMethod = rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoadingConfiguration: true,
              channelConfigurationError: undefined,
            }),
          ),
          switchMap(deviceId => {
            const categories$ = store.categories()
              ? of(store.categories()!)
              : deviceService
                  .getCategories()
                  .pipe(tap(categories => patchState(store, { categories })));
            return categories$.pipe(
              switchMap(categories =>
                deviceService.getChannelConfiguration(deviceId).pipe(
                  tapResponse({
                    next: (data: DeviceChannelConfiguration) => {
                      forEach(data.channels, channel => {
                        channel.category = categories.find(c => c.id == channel.categoryId)?.name;
                      });
                      patchState(store, {
                        isLoadingConfiguration: false,
                        channelConfiguration: data,
                        channelConfigurationError: undefined,
                      });
                    },
                    error: (error: unknown) => {
                      patchState(store, {
                        isLoadingConfiguration: false,
                        channelConfiguration: undefined,
                        channelConfigurationError: classifyChannelConfigurationError(error),
                      });
                    },
                  }),
                ),
              ),
            );
          }),
        ),
      );
      return {
        getChannelConfigurationMethod,
        getCategoriesMethod,
        refreshDevicesMethod: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(() => {
              return deviceService.getListDevicesFilter(store.filter()).pipe(
                tapResponse({
                  next: (data: DeviceResponseModel) => {
                    patchState(store, {
                      deviceData: data,
                      isLoading: false,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        getDevicesMethod: rxMethod<number>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap((pageIndex: number) => {
              const filter: DeviceFiltersModel = {
                ...store.filter(),
                offset: pageIndex,
              };

              patchState(store, { filter });
              return deviceService.getListDevicesFilter(filter).pipe(
                tapResponse({
                  next: (data: DeviceResponseModel) => {
                    patchState(store, {
                      deviceData: data,
                      isLoading: false,
                      filter,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        getDevicesFilterMethod: rxMethod<DevicePayloadModel>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(payload => {
              let filter = {
                ...store.filter(),
                ...payload,
                offset: initialState.filter.offset,
              };
              filter = pickBy(filter, (value: any) => value !== '') as any;
              patchState(store, { filter });
              return deviceService.getListDevicesFilter(filter).pipe(
                tapResponse({
                  next: (data: any) => {
                    patchState(store, {
                      deviceData: data,
                      isLoading: false,
                      filter,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        getDeviceTypesMethod: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(() => {
              return deviceService.getDeviceTypes().pipe(
                tapResponse({
                  next: (data: DeviceTypeResponseModel) => {
                    patchState(store, {
                      deviceTypes: data,
                      isLoading: false,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),
        getDeviceDetailMethod: rxMethod<string>(
          pipe(
            // Clear the cached channel configuration when switching devices.
            // This store is root-scoped, so a previous device's channels would
            // otherwise linger and make the settings tab's PV card think the
            // configuration is already loaded — skipping the fetch for the
            // current device until the Configuration tab forces a reload.
            tap(() =>
              patchState(store, {
                isLoading: true,
                channelConfiguration: undefined,
                channelConfigurationError: undefined,
                phaseCountReadForbidden: false,
              }),
            ),
            switchMap(deviceId => {
              return deviceService.getDeviceDetail(deviceId).pipe(
                tapResponse({
                  next: (data: DeviceModel) => {
                    partnerService
                      .getCompanyDetail(data.partnerOid)
                      .pipe(
                        tapResponse({
                          next: (data: any) => {
                            patchState(store, {
                              partnerByDeviceId: data,
                              isLoading: false,
                            });
                          },
                          error: () => {
                            patchState(store, { isLoading: false });
                          },
                        }),
                      )
                      .subscribe();
                    if (data.locationId) {
                      locationService
                        .getLocationDetail(data.locationId)
                        .pipe(
                          tapResponse({
                            next: (data: any) => {
                              patchState(store, {
                                locationByDeviceId: data,
                                isLoading: false,
                              });
                            },
                            error: () => {
                              patchState(store, {
                                locationByDeviceId: undefined,
                                isLoading: false,
                              });
                            },
                          }),
                        )
                        .subscribe();
                    } else {
                      patchState(store, {
                        locationByDeviceId: undefined,
                        isLoading: false,
                      });
                    }
                    statusService
                      .getLatestStatus(data.deviceId)
                      .pipe(
                        tapResponse({
                          next: (data: LatestStatusResponse) => {
                            patchState(store, {
                              deviceDetail: {
                                ...store.deviceDetail()!,
                                internalDeviceStatus: data,
                              },
                            });
                          },
                          error: () => {
                            patchState(store, { isLoading: false });
                          },
                        }),
                      )
                      .subscribe();
                    // GET /devices/{id} doesn't carry phaseCount (fase-detectie-backend/b1
                    // added a dedicated endpoint instead, same as meterRole has no shared
                    // precedent here) - merge it in the same way as internalDeviceStatus above.
                    deviceService
                      .getPhaseCount(data.deviceId)
                      .pipe(
                        tapResponse({
                          next: (response: {
                            phaseCount: PhaseCount | null;
                            phaseCountFixed?: boolean;
                          }) => {
                            patchState(store, {
                              deviceDetail: {
                                ...store.deviceDetail()!,
                                // Empty stays empty: null means detection has never run, which
                                // is not the same state as the 'unknown' the job writes once it
                                // has looked and stayed inconclusive.
                                phaseCount: response.phaseCount,
                                phaseCountFixed: response.phaseCountFixed ?? false,
                              },
                            });
                          },
                          // Non-fatal - settle on 'unknown' so the settings tab still has a
                          // defined value to seed its unsaved-changes comparison against,
                          // instead of leaving phaseCount undefined forever (the settings tab's
                          // seeding effect waits for phaseCount !== undefined and would otherwise
                          // never run).
                          error: (error: unknown) => {
                            patchState(store, {
                              deviceDetail: {
                                ...store.deviceDetail()!,
                                phaseCount: 'unknown',
                              },
                              phaseCountReadForbidden:
                                error instanceof HttpErrorResponse && error.status === 403,
                            });
                          },
                        }),
                      )
                      .subscribe();
                    patchState(store, {
                      deviceDetail: data,
                      isLoading: false,
                      isExecutingConnectionTest: false,
                      connectionTestResult: undefined,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),
        getDeviceConfigurationsForCategoryMethod: rxMethod<number>(
          pipe(
            tap(() => patchState(store, { isLoadingDeviceConfigurations: true })),
            switchMap(categoryId => {
              return deviceService
                .getDeviceConfigurationsForDeviceChannel(store.deviceDetail()!.deviceId, categoryId)
                .pipe(
                  tapResponse({
                    next: deviceConfigurations => {
                      patchState(store, {
                        deviceConfigurations: deviceConfigurations.deviceConfigurations,
                        isLoadingDeviceConfigurations: false,
                      });
                    },
                    error: () => patchState(store, { isLoadingDeviceConfigurations: false }),
                  }),
                );
            }),
          ),
        ),
        getDeviceConfigurationsForChannelNumber: rxMethod<number>(
          pipe(
            tap(() => patchState(store, { isLoadingDeviceConfigurations: true })),
            switchMap(channelNumber => {
              return deviceService
                .getDeviceConfigurationsForChannelNumber(
                  store.deviceDetail()!.deviceId,
                  channelNumber,
                )
                .pipe(
                  tapResponse({
                    next: deviceConfigurations => {
                      patchState(store, {
                        deviceConfigurations: deviceConfigurations.deviceConfigurations,
                        isLoadingDeviceConfigurations: false,
                      });
                    },
                    error: () => patchState(store, { isLoadingDeviceConfigurations: false }),
                  }),
                );
            }),
          ),
        ),
        sendDeviceConfigurationFileToDeviceMethod: rxMethod<DeviceConfigurationUpdate>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(deviceConfigurationUpdate => {
              return deviceService
                .sendDeviceConfigurationFileToDevice(
                  store.deviceDetail()!.deviceId,
                  deviceConfigurationUpdate,
                )
                .pipe(
                  switchMap(({ deviceConfigDispatchId }) => {
                    const result = interval(1000).pipe(
                      concatMap(() =>
                        deviceService.sendDeviceConfigurationDispatch(deviceConfigDispatchId),
                      ),
                      first(data => !['Pending', 'In Progress'].includes(data.status)),
                      tap(() => getChannelConfigurationMethod(store.deviceDetail()!.deviceId)),
                    );
                    return result;
                  }),
                );
            }),
          ),
        ),
        clearDeviceConfigurationFileFromDeviceMethod: rxMethod<DeviceConfigurationClear>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(deviceConfigurationClear => {
              return deviceService
                .clearDeviceConfigurationFileFromDevice(
                  store.deviceDetail()!.deviceId,
                  deviceConfigurationClear,
                )
                .pipe(
                  switchMap(({ deviceConfigDispatchId }) => {
                    const result = interval(1000).pipe(
                      concatMap(() =>
                        deviceService.sendDeviceConfigurationDispatch(deviceConfigDispatchId),
                      ),
                      first(data => !['Pending', 'In Progress'].includes(data.status)),
                      tap(() => getChannelConfigurationMethod(store.deviceDetail()!.deviceId)),
                    );
                    return result;
                  }),
                );
            }),
          ),
        ),

        updateChannelSettingMethod: rxMethod<ChannelUpdatePayload>(
          pipe(
            switchMap(payload => {
              return deviceService
                .updateChannelSetting(store.deviceDetail()!.deviceId, payload)
                .pipe(
                  tapResponse({
                    next: () => {},
                    error: () => {},
                  }),
                );
            }),
          ),
        ),
        executeConnectionTest: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(store, {
                isExecutingConnectionTest: true,
                connectionTestResult: undefined,
              }),
            ),
            switchMap(() => deviceService.executeConnectionTest(store.deviceDetail()!.deviceId)),
            tapResponse({
              next: result => {
                patchState(store, {
                  isExecutingConnectionTest: false,
                  connectionTestResult: result,
                });
              },
              error: () => {
                patchState(store, {
                  isExecutingConnectionTest: false,
                  connectionTestResult: undefined,
                });
              },
            }),
          ),
        ),

        getVerificationAttempts: rxMethod<{
          deviceId: number;
          pageIndex: number | undefined;
          pageSize: number | undefined;
        }>(
          pipe(
            tap(() => patchState(store, { isLoadingVerifications: true })),
            switchMap(({ deviceId, pageIndex, pageSize }) => {
              return deviceService.getVerificationAttempts(deviceId, pageSize, pageIndex).pipe(
                tapResponse({
                  next: (data: DeviceVerifications) => {
                    patchState(store, {
                      verificationAttempts: data,
                      isLoadingVerifications: false,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoadingVerifications: false });
                  },
                }),
              );
            }),
          ),
        ),
        getVerificationDetails: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoadingVerificationDetails: true })),
            switchMap(verificationId => {
              return deviceService.getVerificationDetails(verificationId).pipe(
                tapResponse({
                  next: (verificationDetails: VerificationDetails) => {
                    patchState(store, { verificationDetails, isLoadingVerificationDetails: false });
                    const matchingVerificationAttempt = store
                      .verificationAttempts()
                      ?.verifications?.find(v => v.verificationId == verificationId);
                    if (matchingVerificationAttempt) {
                      matchingVerificationAttempt.error = verificationDetails.error;
                      matchingVerificationAttempt.status = verificationDetails.status;
                      patchState(store, { verificationAttempts: store.verificationAttempts() });
                    }
                  },
                  error: () => {
                    patchState(store, {
                      verificationDetails: undefined,
                      isLoadingVerificationDetails: false,
                    });
                  },
                }),
              );
            }),
          ),
        ),
        getInitialConfigFilesForDeviceMethod: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, {
                isLoadingInitialConfigFiles: true,
                initialConfigFiles: undefined,
              }),
            ),
            switchMap(deviceId => {
              return deviceService.getInitialConfigFilesForDevice(deviceId).pipe(
                tapResponse({
                  next: initialConfigFiles => {
                    patchState(store, { initialConfigFiles, isLoadingInitialConfigFiles: false });
                  },
                  error: () => patchState(store, { isLoadingInitialConfigFiles: false }),
                }),
              );
            }),
          ),
        ),
        sendInitialConfigToDeviceMethod: rxMethod<{ deviceId: string; fileId: number }>(
          pipe(
            switchMap(({ deviceId, fileId }) => {
              return deviceService.sendInitialConfigToDevice(deviceId, fileId).pipe(
                switchMap(({ initialConfigDispatchId }) => {
                  return interval(1000).pipe(
                    concatMap(() =>
                      deviceService.getInitialConfigDispatchStatus(initialConfigDispatchId),
                    ),
                    first(data => !['Pending', 'In Progress'].includes(data.status)),
                    tap(() => getChannelConfigurationMethod(store.deviceDetail()!.deviceId)),
                  );
                }),
              );
            }),
          ),
        ),
        /** Load the installation groups the device belongs to (directly or via its location). */
        getInstallationGroupsByDeviceId: rxMethod<number>(
          pipe(
            tap(() => patchState(store, { isLoadingInstallationGroups: true })),
            switchMap(deviceId => fetchInstallationGroupsForDevice(deviceId)),
          ),
        ),

        performVerication(deviceId: string): Observable<StartVerificationResult> {
          return deviceService.performVerification(deviceId);
        },
        performReadout(deviceId: string): Observable<StartReadoutResult> {
          return deviceService.performReadout(deviceId);
        },
        refreshSim(deviceId: string): Observable<SimStatusResult> {
          return deviceService.refreshSim(deviceId);
        },
        topUpSim(deviceId: string, iccid: string): Observable<SimActionResult> {
          return deviceService.topUpSim(deviceId, iccid);
        },
        extendSim(deviceId: string, iccid: string): Observable<SimActionResult> {
          return deviceService.extendSim(deviceId, iccid);
        },
        getReadoutStatus: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoadingReadoutStatus: true })),
            switchMap(polId => {
              return deviceService.getReadoutStatus(polId).pipe(
                tapResponse({
                  next: (readoutStatus: ReadoutStatus) => {
                    patchState(store, { readoutStatus, isLoadingReadoutStatus: false });
                  },
                  error: () => {
                    patchState(store, { readoutStatus: undefined, isLoadingReadoutStatus: false });
                  },
                }),
              );
            }),
          ),
        ),

        // Firmware management covers every firmware-capable device type (EnnyLCU and DLMS alike),
        // so no device-type filter is applied here.
        getFirmwareDevicesMethod: rxMethod<number>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap((pageIndex: number) => {
              const filter: DeviceFiltersModel = {
                ...store.filter(),
                offset: pageIndex,
              };

              patchState(store, { filter });
              return deviceService.getListDevicesFilter(filter).pipe(
                tapResponse({
                  next: (data: DeviceResponseModel) => {
                    patchState(store, {
                      deviceData: data,
                      isLoading: false,
                      filter,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),

        refreshFirmwareDevicesMethod: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(() => {
              const filter: DeviceFiltersModel = {
                ...store.filter(),
              };
              return deviceService.getListDevicesFilter(filter).pipe(
                tapResponse({
                  next: (data: DeviceResponseModel) => {
                    patchState(store, {
                      deviceData: data,
                      isLoading: false,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoading: false });
                  },
                }),
              );
            }),
          ),
        ),
        // Reconciles the store's cached DeviceModel right after a phase-count save, same
        // convention as meter_role's setDeviceMeterRole (RDT-592) — every consumer reflects it
        // immediately without waiting for a full getDeviceDetailMethod re-fetch.
        setDevicePhaseCount(phaseCount: PhaseCount): void {
          const current = store.deviceDetail();
          if (!current) return;
          patchState(store, { deviceDetail: { ...current, phaseCount } });
        },
        // Reconciles the store's cached DeviceModel right after a meter-role save so every
        // consumer (e.g. the device-detail page header badge) reflects it immediately, without
        // waiting for a full getDeviceDetailMethod re-fetch.
        setDeviceMeterRole(meterRole: MeterRole): void {
          const current = store.deviceDetail();
          if (!current) return;
          patchState(store, { deviceDetail: { ...current, meterRole } });
        },
        // Reconciles the store's cached DeviceModel right after a refresh-sim call (and after
        // top-up/extend, which don't return a full status themselves - see DeviceDetailComponent)
        // so the general tab reflects the outcome in-place, same convention as
        // setDevicePhaseCount/setDeviceMeterRole above, without a full getDeviceDetailMethod re-fetch.
        setDeviceSimStatus(result: SimStatusResult): void {
          const current = store.deviceDetail();
          if (!current) return;
          patchState(store, {
            deviceDetail: {
              ...current,
              simIccid: result.iccid,
              simStatus: result.sessionStatus,
              simLifecycleStatus: result.lifecycleStatus,
              simDataRemainingMb: result.dataRemainingMb,
              simDataTotalMb: result.dataTotalMb,
              simEndDate: result.endDate,
              simLastSyncedAt: result.lastSyncedAt,
            },
          });
        },
      };
    },
  ),
);
